import { useEffect, useState, useMemo } from "react";
import { ForecastInput } from "./ForecastInput";
import { monthKeysForecastEnum } from "../../consts/quickbooksReports";
import { monthsLowerCase } from "../../consts/forecast";
import { CheckIcon } from "@heroicons/react/24/outline";

// Utility: shallow compare key/values of two objects
const isSameObject = (a = {}, b = {}) => {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
        if (a[key] !== b[key]) return false;
    }
    return true;
};

export const MonthlyAllocationSection = ({
    monthsData,
    access,
    onSaveMonthsData
}) => {
    const [monthlyData, setMonthlyData] = useState(monthsData);

    const calculatePercentages = (data) => {
        if (Object.keys(data).length === 0) return 0;
        const sum = Object.values(data).reduce((acc, val) => acc + (parseFloat(val) || 0), 0);
        return Number(sum.toFixed(2));
    };

    const percentagesByMonths = useMemo(() => calculatePercentages(monthlyData), [monthlyData]);

    useEffect(() => {
        // Update local state only if incoming data actually changed to avoid render loops
        if (!isSameObject(monthsData, monthlyData)) {
            setMonthlyData(monthsData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [monthsData]);
    // Removed the second effect that set state based on monthlyData to avoid infinite updates.

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <span className="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-base font-medium">
                    Monthly Allocation (%)
                </span>
                <div className="flex items-center gap-2">
                    <div className={`flex items-center gap-1 ${percentagesByMonths === 100 ? "text-green-600" : "text-red-600"}`}>
                        <CheckIcon className="h-4 w-4" />
                        <span className="text-sm font-medium">{percentagesByMonths}%</span>
                    </div>
                </div>
            </div>
            {Object.keys(monthlyData).length > 0 && (
                <div className="grid grid-cols-6 gap-3">
                    {monthsLowerCase
                        .filter((key) => Object.prototype.hasOwnProperty.call(monthlyData, key))
                        .map((key) => {
                        const value = monthlyData[key];
                        const label = monthKeysForecastEnum[key];
                        return (
                            <ForecastInput
                                key={key}
                                id={label}
                                label={label}
                                access={access}
                                inputValue={value}
                                setInputValue={(newValue) => {
                                    const numericValue = Number(newValue);
                                    const valueToSet = Number.isFinite(numericValue) ? numericValue : 0;
                                    setMonthlyData((prev) => {
                                        const updated = {
                                            ...prev,
                                            [key]: valueToSet,
                                        };
                                        onSaveMonthsData?.(updated);
                                        return updated;
                                    });
                                }}
                            />
                        )
                    })}
                </div>  
            )}
            {percentagesByMonths !== 100 && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <div className="flex items-center gap-2 text-red-700">
                        <span className="text-sm font-medium">
                            Total allocation must equal 100%. Current total: {percentagesByMonths}%
                        </span>
                    </div>
                </div>
            )}
            {access === "edit" && (
                <div className="flex gap-3">
                    <button
                        className="ring-offset-background inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors border-input bg-background hover:bg-gray-100 border h-10 px-4 py-2"
                        onClick={(e) => {
                            e.stopPropagation();
                            const data = {
                                "jan": 8.33,
                                "feb": 8.33,
                                "mar": 8.33,
                                "apr": 8.33,
                                "may": 8.33,
                                "jun": 8.33,
                                "jul": 8.33,
                                "aug": 8.33,
                                "sep": 8.33,
                                "oct": 8.33,
                                "nov": 8.33,
                                "dec": 8.37,
                            };
                            setMonthlyData(data);
                            onSaveMonthsData?.(data);
                        }}
                    >
                        Reset to Flat (8.33%)
                    </button>
                    <button
                        className="ring-offset-background inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors border-input bg-background hover:bg-gray-100 border h-10 px-4 py-2"
                        onClick={(e) => {
                            e.stopPropagation();
                            const data = {
                                "jan": 5,
                                "feb": 5,
                                "mar": 6,
                                "apr": 6,
                                "may": 7,
                                "jun": 7,
                                "jul": 8,
                                "aug": 8,
                                "sep": 9,
                                "oct": 12,
                                "nov": 15,
                                "dec": 12,
                            };
                            setMonthlyData(data);
                            onSaveMonthsData?.(data);
                        }}
                    >
                        Q4 Heavy Template
                    </button>
                </div>
            )}
        </div>
    )
}
